import React, { createContext, useContext, useState, useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, Search, Package, Camera, BarChart3, LogOut, AlertTriangle, Settings, RotateCcw } from 'lucide-react'
import { useTheme } from '../contexts/ThemeContext'
import { useAuth } from '../contexts/AuthContext'
import { useMobile } from '../contexts/MobileContext'
import { useNotification } from '../contexts/NotificationContext'
import { Button } from './ui/Button'
import { Badge } from './ui/Badge'

export interface PageConfig {
  id: string
  title: string
  description?: string
  actions?: Array<{
    id: string
    label: string
    variant?: 'primary' | 'secondary' | 'destructive'
    onClick: () => void
  }>
  layout?: {
    containerWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
    columns?: number
  }
  breadcrumbs?: Array<{
    label: string
    href?: string
  }>
}

interface ShellContextType {
  openPanel: (panel: React.ReactNode) => void
  closePanel: () => void
  isPanelOpen: boolean
  showConfirm: (message: string, onConfirm: () => void, onCancel?: () => void) => void
  hideConfirm: () => void
  confirmDialog: {
    message: string
    onConfirm: () => void
    onCancel?: () => void
  } | null
  toast: (toast: { title: string; description?: string; type: 'success' | 'error' | 'warning' | 'info' }) => void
}

const ShellContext = createContext<ShellContextType | undefined>(undefined)

export function useShell() {
  const context = useContext(ShellContext)
  if (context === undefined) {
    throw new Error('useShell must be used within a PageShell')
  }
  return context
}

interface PageShellProps {
  config: PageConfig
  children: React.ReactNode
}

export function PageShell({ config, children }: PageShellProps) {
  const { setTheme, resolvedTheme } = useTheme()
  const { user, logout } = useAuth()
  const { isMobile } = useMobile()
  const { success, error, warning, info } = useNotification()
  const location = useLocation()
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [panel, setPanel] = useState<React.ReactNode>(null)
  const [confirmDialog, setConfirmDialog] = useState<{
    message: string
    onConfirm: () => void
    onCancel?: () => void
  } | null>(null)

  const openPanel = useCallback((panel: React.ReactNode) => {
    setPanel(panel)
  }, [])

  const closePanel = useCallback(() => {
    setPanel(null)
  }, [])

  const showConfirm = useCallback((message: string, onConfirm: () => void, onCancel?: () => void) => {
    setConfirmDialog({ message, onConfirm, onCancel })
  }, [])

  const hideConfirm = useCallback(() => {
    setConfirmDialog(null)
  }, [])

  const toast = useCallback((toastData: { title: string; description?: string; type: 'success' | 'error' | 'warning' | 'info' }) => {
    if (toastData.type === 'success') success(toastData.title, toastData.description)
    else if (toastData.type === 'error') error(toastData.title, toastData.description)
    else if (toastData.type === 'warning') warning(toastData.title, toastData.description)
    else if (toastData.type === 'info') info(toastData.title, toastData.description)
  }, [success, error, warning, info])

  const shellContext: ShellContextType = {
    openPanel,
    closePanel,
    isPanelOpen: !!panel,
    showConfirm,
    hideConfirm,
    confirmDialog,
    toast,
  }

  const navigation = [
    { name: 'Home', href: '/', icon: BarChart3 },
    { name: 'Intake', href: '/intake', icon: Package },
    { name: 'Search', href: '/search', icon: Search },
    { name: 'Scanner', href: '/qr-scanner', icon: Camera },
    { name: 'Returns', href: '/returns', icon: RotateCcw },
    { name: 'Totes', href: '/admin/totes', icon: Package },
    { name: 'Admin', href: '/admin', icon: Settings },
  ]

  const containerWidth = config.layout?.containerWidth || 'xl'
  const containerClasses = {
    sm: 'max-w-2xl',
    md: 'max-w-4xl',
    lg: 'max-w-6xl',
    xl: 'max-w-7xl',
    '2xl': 'max-w-8xl'
  }

  return (
    <ShellContext.Provider value={shellContext}>
      <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)', fontFamily: 'Rajdhani, sans-serif' }}>
        {/* Header */}
        <header style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          width: '100%',
          borderBottom: '2px solid rgba(251, 146, 60, 0.3)',
          background: 'rgba(26, 31, 46, 0.95)',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 0 30px rgba(251, 146, 60, 0.2)'
        }}>
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {config.breadcrumbs && config.breadcrumbs.length > 0 && (
                  <nav className="flex items-center space-x-2 text-sm" style={{ color: '#8A95AA' }}>
                    {config.breadcrumbs.map((crumb, index) => (
                      <React.Fragment key={index}>
                        {index > 0 && <span>/</span>}
                        {crumb.href ? (
                          <Link to={crumb.href} style={{ color: '#FB923C', textDecoration: 'none' }}>
                            {crumb.label}
                          </Link>
                        ) : (
                          <span>{crumb.label}</span>
                        )}
                      </React.Fragment>
                    ))}
                  </nav>
                )}
              </div>

              <div className="flex items-center space-x-2">
                {/* Desktop Navigation */}
                {!isMobile && (
                  <nav className="flex items-center space-x-2">
                    {navigation.map((item) => {
                      const isActive = location.pathname === item.href
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px 20px',
                            borderRadius: '12px',
                            transition: 'all 0.3s',
                            backgroundColor: 'transparent',
                            color: isActive ? '#FB923C' : '#E0E6F0',
                            border: isActive ? '2px solid #FB923C' : '2px solid rgba(251, 146, 60, 0.3)',
                            textDecoration: 'none',
                            fontSize: '14px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            boxShadow: isActive ? '0 0 20px rgba(251, 146, 60, 0.5), inset 0 0 20px rgba(251, 146, 60, 0.1)' : 'none'
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.borderColor = 'rgba(251, 146, 60, 0.6)'
                              e.currentTarget.style.boxShadow = '0 0 15px rgba(251, 146, 60, 0.3)'
                              e.currentTarget.style.color = '#FB923C'
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.borderColor = 'rgba(251, 146, 60, 0.3)'
                              e.currentTarget.style.boxShadow = 'none'
                              e.currentTarget.style.color = '#E0E6F0'
                            }
                          }}
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.name}</span>
                        </Link>
                      )
                    })}
                  </nav>
                )}

                {/* User Menu */}
                {user && (
                  <div className="flex items-center space-x-2">
                    <div style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      background: 'rgba(251, 146, 60, 0.15)',
                      border: '1px solid rgba(251, 146, 60, 0.3)',
                      color: '#FB923C',
                      fontSize: '12px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      {user.name}
                    </div>
                    <button
                      onClick={logout}
                      style={{
                        padding: '8px',
                        borderRadius: '6px',
                        background: 'transparent',
                        border: '1px solid rgba(251, 146, 60, 0.3)',
                        color: '#E0E6F0',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.2)'
                        e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.5)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent'
                        e.currentTarget.style.borderColor = 'rgba(251, 146, 60, 0.3)'
                      }}
                    >
                      <LogOut className="h-4 w-4" />
                    </button>
                  </div>
                )}

                {/* Mobile Menu Button */}
                {isMobile && (
                  <button
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    style={{
                      padding: '8px',
                      borderRadius: '6px',
                      background: 'transparent',
                      border: '1px solid rgba(251, 146, 60, 0.3)',
                      color: '#E0E6F0',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                  </button>
                )}
              </div>
            </div>

            {/* Mobile Navigation */}
            {isMobile && mobileMenuOpen && (
              <div className="mt-4 pb-4" style={{ borderTop: '1px solid rgba(251, 146, 60, 0.3)' }}>
                <div className="flex flex-col space-y-2 pt-4">
                  {navigation.map((item) => {
                    const isActive = location.pathname === item.href
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px 16px',
                          borderRadius: '8px',
                          transition: 'all 0.2s',
                          backgroundColor: isActive ? '#FB923C' : 'transparent',
                          color: isActive ? '#FFFFFF' : '#E0E6F0',
                          border: isActive ? 'none' : '1px solid rgba(251, 146, 60, 0.2)',
                          textDecoration: 'none',
                          fontSize: '14px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          boxShadow: isActive ? '0 0 20px rgba(251, 146, 60, 0.4)' : 'none'
                        }}
                      >
                        <item.icon className="h-5 w-5" />
                        <span>{item.name}</span>
                      </Link>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1">
          <div className={`container mx-auto px-4 py-8 ${containerClasses[containerWidth]}`}>
            {children}
          </div>
        </main>

        {/* Actions */}
        {config.actions && config.actions.length > 0 && (
          <div className="sticky bottom-0 z-40 w-full border-t border-outline bg-surface/95 backdrop-blur supports-[backdrop-filter]:bg-surface/60">
            <div className="container mx-auto px-4 py-4">
              <div className="flex justify-end space-x-2">
                {config.actions.map((action) => (
                  <Button
                    key={action.id}
                    variant={action.variant || 'primary'}
                    onClick={action.onClick}
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Panel Overlay */}
        {panel && (
          <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm">
            <div className="absolute right-0 top-0 h-full w-96 bg-surface border-l border-outline shadow-xl">
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-content">Panel</h3>
                  <Button variant="ghost" size="sm" onClick={closePanel}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                {panel}
              </div>
            </div>
          </div>
        )}

        {/* Confirm Dialog */}
        {confirmDialog && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-surface p-6 rounded-lg shadow-lg max-w-md w-full border border-outline">
              <div className="text-center mb-4">
                <AlertTriangle className="h-12 w-12 text-warning mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-content">Confirm Action</h3>
              </div>
              <p className="text-content-secondary mb-6 text-center">{confirmDialog.message}</p>
              <div className="flex justify-center gap-4">
                <Button variant="secondary" onClick={confirmDialog.onCancel || hideConfirm}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={() => { confirmDialog.onConfirm(); hideConfirm(); }}>
                  Confirm
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ShellContext.Provider>
  )
}
